// Text on a path: lays a text layer's characters along its own path, one glyph at a time.

import { flattenPath, parseSvgPath, pathToSvg, type PathCmd } from './geometry.ts';
import { applyCase, layoutRichText, layoutText, measureString, textStyle, type PieceStyle } from './text.ts';
import type { NodeData, Rect, Vec2 } from './types.ts';

export interface PathGlyph {
  text: string;
  /** glyph origin (left end of its baseline), node-local */
  x: number;
  y: number;
  /** radians */
  angle: number;
  width: number;
  /** index into PathTextLayout.styles */
  style: number;
}

export interface PathTextLayout {
  glyphs: PathGlyph[];
  styles: PieceStyle[];
  /** path length and text width, for handles and fitting */
  length: number;
  textWidth: number;
  bounds: Rect;
}

interface Walker {
  length: number;
  closed: boolean;
  at(d: number): { p: Vec2; t: Vec2 };
}

function walker(d: string): Walker | null {
  const poly = flattenPath(parseSvgPath(d), 0.2)[0];
  if (!poly || poly.points.length < 2) return null;
  const pts = poly.closed ? [...poly.points, poly.points[0]] : poly.points;
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const length = cum[cum.length - 1];
  return {
    length,
    closed: poly.closed,
    at(dist) {
      let lo = 0,
        hi = cum.length - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (cum[mid] <= dist) lo = mid;
        else hi = mid;
      }
      const a = pts[lo],
        b = pts[hi];
      const seg = cum[hi] - cum[lo] || 1;
      const k = Math.max(0, Math.min(1, (dist - cum[lo]) / seg));
      const tl = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      return { p: { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k }, t: { x: (b.x - a.x) / tl, y: (b.y - a.y) / tl } };
    },
  };
}

export function layoutTextOnPath(n: NodeData): PathTextLayout {
  const style = textStyle(n);
  const empty: PathTextLayout = { glyphs: [], styles: [], length: 0, textWidth: 0, bounds: { x: 0, y: 0, width: 0, height: 0 } };
  const w = n.textPath ? walker(n.textPath) : null;
  if (!w) return empty;
  // one line: line breaks become spaces (offsets stay the same, so style runs still line up)
  const chars = (n.characters ?? '').replace(/\n/g, ' ');
  const line = n.textRuns?.length ? layoutRichText(chars, style, n.textRuns, Infinity) : layoutText(chars, style, Infinity);
  const styles: PieceStyle[] = line.styles ?? [{ font: line.font, fontSize: style.fontSize, letterSpacing: style.letterSpacing, decoration: style.decoration }];
  const pieces = line.lines[0]?.pieces ?? [{ text: applyCase(chars, style.textCase), style: 0, x: 0, width: line.width }];
  // per-character advances, measured cumulatively so kerning inside a piece is kept
  const adv: { text: string; x: number; width: number; style: number }[] = [];
  let x = 0;
  for (const pc of pieces) {
    const st = styles[pc.style];
    let prev = 0;
    let acc = '';
    for (const ch of pc.text) {
      acc += ch;
      const cur = measureString(acc, st.font, st.letterSpacing, st.features);
      adv.push({ text: ch, x: x + prev, width: cur - prev, style: pc.style });
      prev = cur;
    }
    x += prev;
  }
  const textWidth = x;
  const align = style.textAlign;
  const start = (align === 'CENTER' ? (w.length - textWidth) / 2 : align === 'RIGHT' ? w.length - textWidth : 0) + (n.textPathOffset ?? 0);
  const glyphs: PathGlyph[] = [];
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const g of adv) {
    let mid = start + g.x + g.width / 2;
    if (w.closed) mid = ((mid % w.length) + w.length) % w.length;
    else if (mid < 0 || mid > w.length) continue;
    const { p, t } = w.at(mid);
    const gx = p.x - (t.x * g.width) / 2,
      gy = p.y - (t.y * g.width) / 2;
    glyphs.push({ text: g.text, x: gx, y: gy, angle: Math.atan2(t.y, t.x), width: g.width, style: g.style });
    const fs = styles[g.style].fontSize;
    minX = Math.min(minX, p.x - fs);
    minY = Math.min(minY, p.y - fs);
    maxX = Math.max(maxX, p.x + fs);
    maxY = Math.max(maxY, p.y + fs);
  }
  const bounds = glyphs.length ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY } : empty.bounds;
  return { glyphs, styles, length: w.length, textWidth, bounds };
}

/** The same path drawn in the opposite direction (moves path text to the other side). */
export function reversePath(d: string): string {
  const cmds = parseSvgPath(d);
  // split into subpaths of segments
  type Seg = Exclude<PathCmd, { c: 'Z' } | { c: 'M' }> & { from: Vec2 };
  const subs: { start: Vec2; segs: Seg[]; closed: boolean }[] = [];
  let cur: (typeof subs)[number] | null = null;
  let pos: Vec2 = { x: 0, y: 0 };
  for (const c of cmds) {
    if (c.c === 'M') {
      cur = { start: { x: c.x, y: c.y }, segs: [], closed: false };
      subs.push(cur);
      pos = { x: c.x, y: c.y };
    } else if (c.c === 'Z') {
      if (cur) cur.closed = true;
      if (cur) pos = cur.start;
    } else if (cur) {
      cur.segs.push({ ...c, from: pos });
      pos = { x: c.x, y: c.y };
    }
  }
  const out: PathCmd[] = [];
  for (const s of subs) {
    const end = s.segs.length ? { x: s.segs[s.segs.length - 1].x, y: s.segs[s.segs.length - 1].y } : s.start;
    out.push({ c: 'M', x: end.x, y: end.y });
    for (let i = s.segs.length - 1; i >= 0; i--) {
      const g = s.segs[i];
      if (g.c === 'C') out.push({ c: 'C', x1: g.x2, y1: g.y2, x2: g.x1, y2: g.y1, x: g.from.x, y: g.from.y });
      else out.push({ c: 'L', x: g.from.x, y: g.from.y });
    }
    if (s.closed) out.push({ c: 'Z' });
  }
  return pathToSvg(out);
}

/** Distance along a path to the point on it nearest to p (node-local). */
export function pathDistanceAt(d: string, p: Vec2): number {
  const poly = flattenPath(parseSvgPath(d), 0.2)[0];
  if (!poly) return 0;
  const pts = poly.closed ? [...poly.points, poly.points[0]] : poly.points;
  let best = Infinity,
    at = 0,
    acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1],
      b = pts[i];
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    const k = len ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (len * len))) : 0;
    const dist = Math.hypot(a.x + dx * k - p.x, a.y + dy * k - p.y);
    if (dist < best) (best = dist), (at = acc + len * k);
    acc += len;
  }
  return at;
}
